import type { ChipOption } from "components/editor/email-editor";
import dayjs from "dayjs";
import type { Hours } from "shared/types/common";
import type { GetTicketByIdResponse } from "shared/types/serviceDesk";
import { isoToDateAndTime } from "utilz/helpers";

// ─── Chip Options ────────────────────────────────────────────────────

export const chatVariableChipOptions: ChipOption[] = [
  // User Details
  { id: "serviceUId", label: "Service ID", value: "service-id" },
  { id: "customerName", label: "Customer name", value: "customer-name" },
  { id: "customerCode", label: "Customer code", value: "customer-code" },
  { id: "userName", label: "Customer user", value: "user-name" },
  { id: "serviceAge", label: "Age", value: "service-age" },
  // { id: "companyName", label: "Company Name", value: "company-name" },
  // { id: "brandName", label: "Company Brand Name", value: "Company-brand-name" },
  // { id: "address1", label: "Company Address", value: "address1" },
  { id: "location", label: "Location", value: "location" },
  { id: "state", label: "State", value: "state" },
  { id: "country", label: "Country", value: "country" },
  // Ticket Details
  { id: "serviceType", label: "Service category", value: "service-type" },
  { id: "serviceSummary", label: "Summary", value: "service-summary" },
  { id: "serviceSubType", label: "Sub-category", value: "service-sub-type" },
  { id: "source", label: "Source", value: "source" },
  { id: "priority", label: "Priority", value: "priority" },
  { id: "urgency", label: "Urgency", value: "urgency" },
  { id: "impact", label: "Impact", value: "impact" },
  { id: "newStatus", label: "Current status", value: "new-status" },
  { id: "slaName", label: "SLA name", value: "sla-name" },
  { id: "tag", label: "Tag", value: "tag" },
  { id: "billableStatus", label: "Billable status", value: "billable-status" },
  { id: "description", label: "Description", value: "description" },
  // Assignment & Time
  { id: "assignee", label: "Assignee", value: "assignee" },
  { id: "sender", label: "Sender", value: "sender" },
  { id: "serviceOwner", label: "Service owner", value: "service-owner" },
  { id: "workRole", label: "Billing role", value: "work-role" },
  { id: "workType", label: "Work type", value: "work-type" },
  // { id: "createdAt", label: "Created time", value: "created-at" },
  // { id: "scheduledTime", label: "Scheduled time", value: "scheduled-time" },
  // { id: "budgetedHours", label: "Budgeted hours", value: "budgeted-hours" },
];

// ─── Helpers ─────────────────────────────────────────────────────────

const stripHtml = (html: string | null | undefined): string => {
  if (!html) return "";
  try {
    const doc = new DOMParser().parseFromString(html, "text/html");
    return (doc.body.textContent || "").trim();
  } catch {
    return "";
  }
};

const titleCase = (value: string | null | undefined): string => {
  if (!value) return "";
  return value.charAt(0).toUpperCase() + value.slice(1);
};

const formatHours = (
  hours: Hours | Record<string, unknown> | null | undefined,
): string => {
  if (!hours || typeof hours !== "object") return "";
  const h = (hours as Hours).hours ?? 0;
  const m = (hours as Hours).minutes ?? 0;
  const parts: string[] = [];
  if (h) parts.push(`${h}h`);
  if (m) parts.push(`${m}m`);
  return parts.join(" ");
};

const joinName = (
  first: string | null | undefined,
  last: string | null | undefined,
): string => `${first ?? ""} ${last ?? ""}`.trim();

const formatTags = (tags: unknown): string => {
  if (!Array.isArray(tags)) return "";
  return tags
    .map(
      (t: { name?: string; tag_name?: string }) => t?.name ?? t?.tag_name ?? "",
    )
    .filter(Boolean)
    .join(", ");
};

const formatAssignees = (ticket: GetTicketByIdResponse): string =>
  (ticket.schedules ?? [])
    .map((s) => s.display_name || joinName(s.first_name, s.last_name))
    .filter(Boolean)
    .join(", ");

const formatAddress = (ticket: GetTicketByIdResponse): string =>
  [ticket.street, ticket.city].filter(Boolean).join(", ");

const formatScheduledTime = (ticket: GetTicketByIdResponse): string => {
  const firstSchedule = (ticket.schedules ?? [])[0];
  if (!firstSchedule?.start_time) return "";
  return isoToDateAndTime({ isodate: firstSchedule.start_time });
};

const formatCreatedAt = (ticket: GetTicketByIdResponse): string =>
  ticket.created_at ? isoToDateAndTime({ isodate: ticket.created_at }) : "";

const formatServiceAge = (ticket: GetTicketByIdResponse): string =>
  ticket.created_at ? dayjs(ticket.created_at).fromNow(true) : "";

const formatUserName = (ticket: GetTicketByIdResponse): string =>
  ticket.customer_user_display_name ||
  joinName(ticket.customer_user_first_name, ticket.customer_user_last_name);

const formatServiceOwner = (ticket: GetTicketByIdResponse): string =>
  ticket.ticket_owner_display_name ||
  joinName(ticket.ticket_owner_first_name, ticket.ticket_owner_last_name);

// ─── Resolver Map ────────────────────────────────────────────────────

type Resolver = (ticket: GetTicketByIdResponse) => string;

const RESOLVERS: Record<string, Resolver> = {
  // User / Customer
  serviceUId: (t) => t.u_id ?? "",
  customerName: (t) => t.customer_name ?? "",
  customerCode: (t) => t.customer_code ?? "",
  userName: formatUserName,
  serviceAge: formatServiceAge,
  companyName: (t) => t.customer_name ?? "",
  brandName: (t) => t.customer_name ?? "",
  address1: formatAddress,
  location: (t) => t.location_name ?? "",
  state: (t) => t.state ?? "",
  country: (t) => t.country ?? "",
  // Ticket Details
  serviceType: (t) => t.type_name ?? "",
  serviceSummary: (t) => t.ticket_summary ?? "",
  serviceSubType: (t) => t.subtype_name ?? "",
  source: (t) => t.source_name ?? "",
  priority: (t) => titleCase(t.priority),
  urgency: (t) => titleCase(t.urgency),
  impact: (t) => titleCase(t.impact),
  newStatus: (t) => t.status_name ?? "",
  slaName: (t) => t.sla_name ?? "",
  tag: (t) => formatTags(t.tags),
  billableStatus: (t) => (t.is_billable ? "Billable" : "Non-billable"),
  description: (t) => stripHtml(t.ticket_description),
  // Assignment & Time
  assignee: formatAssignees,
  sender: (t) => joinName(t.created_by_first_name, t.created_by_last_name),
  serviceOwner: formatServiceOwner,
  workRole: (t) => t.work_role_name ?? "",
  workType: (t) => t.work_type_name ?? "",
  createdAt: formatCreatedAt,
  scheduledTime: formatScheduledTime,
  budgetedHours: (t) => formatHours(t.budgeted_hours),
};

// ─── Resolver ────────────────────────────────────────────────────────

/**
 * Resolves a variable chip id to its literal value from the current ticket.
 *
 * Returns an empty string for missing fields or when the ticket is not yet
 * loaded. Never throws.
 */
export const resolveChatVariable = (
  id: string,
  ticket: GetTicketByIdResponse | null | undefined,
): string => {
  if (!ticket) return "";
  const resolver = RESOLVERS[id];
  return resolver ? resolver(ticket) : "";
};
